/*----------------------------------
- MARKETING PAGE: FAQ SECTION
----------------------------------*/

/**
 * The questions of a product feature page, a solutions page or the API page (`section#faq.mk-sec.wrap > div.wp-faq`):
 * the title and "Contact me" at the left (and the API page's aside under it), the answers at the right on the marketing
 * `Faq` part (one kit `Disclosure` each). Each answer keeps its glossary bubbles and its link, and opening one fires the
 * shipped `faq_expand` event.
 *
 * @docs docs/features/website-product-feature-pages
 * @rule The questions are the catalog list the route publishes as FAQPage structured data (`toStructuredDataFaqs`),
 *       so the page and its JSON-LD never disagree.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { ComponentChildren } from 'preact';

import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import { Faq } from '@uniquedomains/components/marketing/faq/Faq';
import type { FaqEntry } from '@uniquedomains/components/marketing/faq/FaqList';

import type { FAQLink } from '@/client/catalogs/landing/faq';
import { MARKETING_FAQ_CONTACT } from '@/client/catalogs/landing/marketingPage';
import MarketingGlossaryText from '@/client/pages/landing/components/marketing-page/MarketingGlossaryText';
import { useFaqExpand } from '@/client/pages/landing/components/marketing-page/useFaqExpand';

/*----------------------------------
- TYPES
----------------------------------*/

export type MarketingFaqItem = {
    question: string;
    answer: string;
    link?: FAQLink;
};

type MarketingFaqSectionProps = {
    title: string;
    /** The line under the title (the solutions pages); the feature pages have none. */
    lead?: string;
    items: readonly MarketingFaqItem[];
    /** Whether an answer's link shows (the solutions pages show a link only to a published human page); every link
     *  shows when absent, as the feature pages did. */
    isLinkVisible?: (link: FAQLink) => boolean;
    /** Drawn under "Contact me" in the head (the API page's "Prefer no integration?" card). */
    aside?: ComponentChildren;
    /** The section's classes: the feature pages' `mk-sec wrap` by default; a page already in its frame passes its own
     *  rhythm (the API page's `wp-sec`). */
    class?: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SECTION_ID = 'faq';

/** Each answer's `details` id, so the expand event names its question without reading the DOM's text. */
const entryId = (index: number) => `faq-${index}`;

/*----------------------------------
- COMPONENT
----------------------------------*/

const MarketingFaqSection = ({ title, lead, items, isLinkVisible, aside, class: klass = 'mk-sec wrap' }: MarketingFaqSectionProps) => {
    const sectionRef = useFaqExpand<HTMLElement>(items.map((item, index) => ({ id: entryId(index), question: item.question })));

    const entries: FaqEntry[] = items.map((item, index) => ({
        id: entryId(index),
        question: item.question,
        // One answer is one glossary section: a term explained in another answer is explained again here, because a
        // reader opening a single question never saw the other one.
        answer: (
            <>
                <p>
                    <MarketingGlossaryText>{item.answer}</MarketingGlossaryText>
                </p>
                {item.link && (!isLinkVisible || isLinkVisible(item.link)) ? <TextLink size="lg" href={item.link.href} label={item.link.label} /> : null}
            </>
        ),
    }));

    return (
        // A plain `section.wrap` rather than `PageWrap`, which takes no ref: the expand listener needs the element.
        <section ref={sectionRef} id={SECTION_ID} class={klass}>
            <Faq title={title} lead={lead} action={{ label: MARKETING_FAQ_CONTACT.label, href: MARKETING_FAQ_CONTACT.href }} aside={aside} entries={entries} />
        </section>
    );
};

export default MarketingFaqSection;
